import * as React from "react";
import { I18nContext } from "../domain/i18n";

interface ImageProps {
  src: string;
  srcSet?: string;
  alt?: string;
  attributes?: any;
  maxWidth?: number;
  aspectRatio: number;
}

function Image(props: ImageProps) {
  const { src, srcSet, alt, attributes = {}, maxWidth, aspectRatio } = props;

  const image = isFinite(aspectRatio) ? (
    <span
      {...attributes}
      className="video__image-aspectRatio"
      style={{ paddingBottom: `${100 / aspectRatio}%`, backgroundImage: `url(${src})` }}
      role="img"
      aria-label={alt}
    />
  ) : (
    <img {...attributes} className="video__image-img" src={src} srcSet={srcSet} alt={alt} />
  );

  if (maxWidth) {
    return (
      <span className="video__image-maxWidth" style={{ maxWidth }}>
        {image}
      </span>
    );
  }

  return image;
}

interface EmbedProps {
  id: string;
  aspectRatio: number;
  title: string;
}

function Embed(props: EmbedProps) {
  const { id, aspectRatio, title } = props;

  const src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=true&showinfo=0&rel=0`;

  return (
    <div
      className="video__embed-container"
      style={{
        paddingBottom: `${100 / aspectRatio}%`,
      }}
    >
      <iframe className="video__embed-iframe" src={src} frameBorder="0" title={title} allowFullScreen />
    </div>
  );
}

export interface VideoProps {
  I18n: I18nContext;
  id: string;
  title: string;
  aspectRatio: number;
}

export function Video({ I18n, id, title, aspectRatio }: VideoProps) {
  const [state, setState] = React.useState({ embedIframe: false });

  const handleClick = React.useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({ embedIframe: true });
  }, []);

  const thumbnail = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

  return (
    <figure className="video">
      {!state.embedIframe && (
        <a className="video__thumbnail" onClick={handleClick}>
          <span className="video__playIcon">
            <PlayIcon />
          </span>
          <span className="video__playNote">{I18n.t("frontend.components.video.do-not-track-note")}</span>
          <Image src={thumbnail} alt="" aspectRatio={aspectRatio} />
        </a>
      )}

      {state.embedIframe && <Embed id={id} aspectRatio={aspectRatio} title={title} />}
      <figcaption>{title}</figcaption>
    </figure>
  );
}

interface PlayIconProps {
  width?: number;
  fill?: string;
}

function PlayIcon(props: PlayIconProps) {
  const { width = 26, fill = "#fff" } = props;

  return (
    <svg width={width} height={width * 1.385} viewBox="0 0 26 36">
      <path d="M25.956 18.188L.894 35.718V.66" fill={fill} />
    </svg>
  );
}
